// services/axiosConfig.js
import axios from 'axios';
import { getAuthToken, removeAuthToken } from '../../utils/auth';
import { toast } from 'react-toastify'; // Optional: For user notifications

const BASE_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: BASE_URL,
});

api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add global response interceptor
api.interceptors.response.use(
  (response) => response, // Pass successful responses through
  (error) => {
    // No response object at all → the request never completed a round-trip:
    // dropped/throttled connection, timeout, DNS failure, or CORS error.
    // Handle this BEFORE the status checks (status is undefined here).
    if (!error.response) {
      const offline =
        error.code === 'ECONNABORTED' ||
        (typeof navigator !== 'undefined' && navigator.onLine === false);
      console.error('Network error:', error.code || error.message);
      toast.error(
        offline
          ? 'No internet connection. Please check your network and try again.'
          : 'Network error — could not reach the server. Please try again.',
        { toastId: 'network-error' } // dedupe: collapse a burst of parallel failures into one toast
      );
      return Promise.reject(error);
    }

    const status = error.response?.status;

    if (status === 400) {
      console.error('Bad Request:', error.response?.data?.message || error.message);
      toast.warning(error.response?.data?.message || 'Bad Request. Please check your input.');
    } else if (status === 401) {
      console.error('Unauthorized: Redirecting to login...');
      toast.error('Your session has expired. Please log in again.');
      removeAuthToken();
      window.location.href = '/login';
    } else if (status === 404) {
      // 404 means a backend route is missing — log it but don't spam the user
      console.warn('API endpoint not found:', error.config?.url);
    } else {
      toast.error(error.response?.data?.message || 'Something went wrong.');
    }

    return Promise.reject(error); // Propagate the error for specific handling elsewhere
  }
);

export default api;